<template>
  <p>
    Utiliza un token de acceso para integrarte a las nuevas APIs de lectura y conectar nuestro MCP con
    otras aplicaciones. Solo puede existir un token a la vez, al caducar o generar uno nuevo, toda
    conexión con el anterior se perderá. Incluye 10,000 API calls gratuitas por mes.
  </p>

  <a :href="apiV2DocumentationUrl" target="_blank" rel="noopener noreferrer">
    <span>
      Ver documentación de APIs v2
      <img :src="externalLinkImage" alt="external-link" />
    </span>
  </a>

  <div class="key__wrapper">
    <label>URL del servidor MCP</label>

    <div class="input__wrapper">
      <div class="input2">
        <a-input :value="serverUrl" disabled placeholder="mcp-connector.tu-dominio.com" />
        <a-tooltip title="Copiar">
          <a-button :disabled="!serverUrl" @click="handleCopy(serverUrl)">
            <template #icon>
              <CopyOutlined />
            </template>
          </a-button>
        </a-tooltip>
      </div>
    </div>
  </div>

  <div class="key__wrapper">
    <label>Token de acceso</label>

    <div class="input__wrapper">
      <div class="input">
        <a-tooltip title="Este token es único y no editable. Para cambiarlo, genera uno nuevo.">
          <a-input-password
            :value="currentToken"
            :disabled="!currentToken"
            :placeholder="currentToken ? 'Token generado' : 'Genere su token de acceso'"
          />
        </a-tooltip>
        <a-tooltip title="Copiar">
          <a-button :disabled="!currentToken" @click="handleCopy(currentToken, 'Token de MCP copiado')">
            <template #icon>
              <CopyOutlined />
            </template>
          </a-button>
        </a-tooltip>
      </div>

      <div class="action">
        <a-button class="button" @click="generateMcpToken">
          Generar Token
        </a-button>
      </div>
    </div>

    <p v-if="currentToken" class="expires">
      Este token caducará el {{ formattedTokenExpiresAt }} 
      <a-tooltip placement="bottom" title="La fecha de caducidad no puede editarse. Si deseas actualizarla, genera un nuevo token.">
        <InfoCircleOutlined />
      </a-tooltip>
    </p>

    <ApiCloudUsageCard v-if="!restrictionMcpToken" />
  </div>

  <ApiCloudPlanRestrictionModal
    v-model:open="openRestriction"
    :plan="dataPlan?.plan"
  />

  <a-modal
    title="Generar token de acceso a MCP"
    :open="openGenerateMcpTokenModal"
    width="480px"
    :bodyStyle="{ padding: '8px 0 16px' }"
    centered
    @cancel="handleCancel"
  >
    <div>
      <p style="margin-bottom: 4px;">
        Establece un periodo de caducidad para el token de acceso al MCP de BeexCC. 
        <strong>Solo puede existir 1 token activo a la vez.</strong>
      </p>
  
      <div class="select-field">
        <label class="select-field__label">
          Periodo de caducidad
          <a-tooltip title="Indica en cuántos días caducará el token de acceso para reforzar su seguridad.">
            <InfoCircleOutlined />
          </a-tooltip>
        </label>
  
        <a-select v-model:value="selectedExpiration" style="width: 100%">
          <a-select-option :value="3">3 días</a-select-option>
          <a-select-option :value="7">7 días</a-select-option>
          <a-select-option :value="15">15 días</a-select-option>
          <a-select-option :value="30">30 días</a-select-option>
          <a-select-option value="custom">Personalizado</a-select-option>
        </a-select>
  
      </div>
  
      <div class="select-field" v-if="selectedExpiration === 'custom'">
        <label class="select-field__label">Cantidad de días</label>
  
        <a-input-number
          v-model:value="customExpirationDays"
          :min="1"
          :max="60"
          @keydown="handleIntegerKeydown"
          style="width: 100%"
        />
        <p class="select-field__help">Máximo 60 días</p>
      </div>
  
      <a-alert type="info" show-icon>
        <template #message>
          Tu token de acceso caducará el <strong>{{ tokenExpirationPreview }}</strong>
        </template>
        <template #icon><CalendarOutlined /></template>
      </a-alert>
    </div>

    <template #footer>
      <a-button :disabled="loading" @click="handleCancel">Cancelar</a-button>
      <a-button type="primary" :loading="loading" @click="generateMcpTokenService">Generar token</a-button>
    </template>

  </a-modal>
</template>


<script setup lang="ts">
import dayjs from "dayjs";
import { computed, createVNode, h, onBeforeUnmount, ref, watch } from "vue";
import { Modal, message } from "ant-design-vue";
import {
  CopyOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons-vue";

import { useCopyToClipboard } from "@/app/shared/composables/useCopyToClipboard";
import ApiCloudPlanRestrictionModal from "@/app/common/components/modals/ApiCloudPlanRestrictionModal.vue";
import ApiCloudUsageCard from "@/app/platformSettings/components/template/ApiCloudUsageCard.vue";
import { apiService } from "@/app/platformSettings/services/ApiService";
import externalLinkImage from "@/app/supervision/assets/external-link.svg?url";

// Documentación de las APIs v2 (Scalar) del ambiente en curso: en producción resuelve a
// https://api-cloud.beexcontact.com/scalar/ (mismo patrón que la doc v1 en ApiKeyTemplate).
const apiV2DocumentationUrl = `https://api-cloud.${import.meta.env.VITE_HOSTNAME}/scalar/`;

const props = defineProps<{
  serverUrl?: string | null;
  token?: string | null;
  tokenExpiresAt?: string | null;
  dataPlan?: any;
}>();


const currentToken = ref(props.token ?? "");
const currentTokenExpiresAt = ref(props.tokenExpiresAt ?? "");
const loading = ref(false);
const openRestriction = ref(false);
const openGenerateMcpTokenModal = ref(false);
const selectedExpiration = ref<number | "custom">(30);
const customExpirationDays = ref(30);
const currentDateTime = ref(dayjs());
let currentDateTimeInterval: ReturnType<typeof setInterval> | null = null;

const isPositiveInteger = /^\d+$/;

const handleIntegerKeydown = (event: KeyboardEvent) => {
  if (event.key.length === 1 && !isPositiveInteger.test(event.key)) event.preventDefault();
};

const handleCopy = useCopyToClipboard();

const startCurrentDateTimeClock = () => {
  if (currentDateTimeInterval) return;

  currentDateTime.value = dayjs();
  currentDateTimeInterval = setInterval(() => {
    currentDateTime.value = dayjs();
  }, 1000);
};

const stopCurrentDateTimeClock = () => {
  if (!currentDateTimeInterval) return;

  clearInterval(currentDateTimeInterval);
  currentDateTimeInterval = null;
};

const expirationInfo = computed(() => {
  const expirationDays =
    selectedExpiration.value === "custom" ? Number(customExpirationDays.value) || 0 : selectedExpiration.value;
  const tokenExpiration = currentDateTime.value.add(expirationDays, "day");

  return {
    days: expirationDays,
    preview: tokenExpiration.format("DD/MM/YYYY [a las] HH:mm"),
    tokenExpiration: tokenExpiration.toISOString(),
  };
});

const tokenExpirationPreview = computed(() => expirationInfo.value.preview);
const formattedTokenExpiresAt = computed(() => {
  if (!currentTokenExpiresAt.value) return "";

  const parsedDate = dayjs(currentTokenExpiresAt.value);
  return parsedDate.isValid() ? parsedDate.format("DD/MM/YYYY [a las] HH:mm") : currentTokenExpiresAt.value;
});

watch(openGenerateMcpTokenModal, (isOpen) => {
  if (isOpen) {
    startCurrentDateTimeClock();
    return;
  }

  stopCurrentDateTimeClock();
});

onBeforeUnmount(() => {
  stopCurrentDateTimeClock();
});

const restrictionMcpToken = computed(() => {
  return props.dataPlan && !props.dataPlan?.configuration?.api_cloud_token?.active;
});

const generateMcpToken = async () => {
  if (restrictionMcpToken.value) {
    openRestriction.value = true;
    return;
  }

  selectedExpiration.value = 30;
  openGenerateMcpTokenModal.value = true;
};

const generateMcpTokenService = async () => {
  if (loading.value) return;

  if (currentToken.value) {
    Modal.confirm({
      icon: createVNode(ExclamationCircleOutlined),
      title: "Al generar el nuevo token, el actual será descartado, ¿deseas continuar?",
      content: h("span", null, [
        "Considera los siguientes puntos:",
        h("ul", { style: { paddingLeft: "20px" } }, [
          h("li", null, ["El token actual dejará de funcionar. Si tienes aplicaciones conectadas mediante este token, ", 
            h("strong", null, "perderán su acceso."), 
          ]),
          h("li", null, "El token actual en este módulo se reemplazará por el nuevo junto a su periodo de caducidad."),
        ]),
      ]),
      okText: "Si, continuar",
      cancelText: "No, cancelar",
      centered: true,
      onOk: () => {
        const tokenExpiration = expirationInfo.value.tokenExpiration;

        loading.value = true;
        void apiService
          .generateMcpToken({
            token_expiration_period: expirationInfo.value.days,
            token_expiration: tokenExpiration,
          })
          .then((token) => {
            currentToken.value = token;
            currentTokenExpiresAt.value = tokenExpiration;
            openGenerateMcpTokenModal.value = false;
            message.success("Token de MCP generado con éxito");
          })
          .catch(() => {
            message.error("No se pudo generar el token de MCP");
          })
          .finally(() => {
            loading.value = false;
          });
      },
    })
  } else {
    Modal.confirm({
      icon: createVNode(ExclamationCircleOutlined),
      title: "Estás a punto de generar el token de acceso al MCP, ¿deseas continuar?",
      content: h("span", null, [
        "El token de acceso caducará en ",
        h("strong", null, [
          `${expirationInfo.value.days} días, el ${expirationInfo.value.preview}.`,
        ]),
        " Si generas uno nuevo antes, este token dejará de funcionar.",
      ]),
      okText: "Si, continuar",
      cancelText: "No, seguir configurando",
      centered: true,
      onOk: () => {
        const tokenExpiration = expirationInfo.value.tokenExpiration;

        loading.value = true;
        void apiService
          .generateMcpToken({
            token_expiration_period: expirationInfo.value.days,
            token_expiration: tokenExpiration,
          })
          .then((token) => {
            currentToken.value = token;
            currentTokenExpiresAt.value = tokenExpiration;
            openGenerateMcpTokenModal.value = false;
            message.success("Token de MCP generado con éxito");
          })
          .catch(() => {
            message.error("No se pudo generar el token de MCP");
          })
          .finally(() => {
            loading.value = false;
          });
      },
    });
  }

};

const handleCancel = () => {
  if (selectedExpiration.value === 30 && customExpirationDays.value === 30) {
    openGenerateMcpTokenModal.value = false;
    return;
  }

  Modal.confirm({
    icon: createVNode(ExclamationCircleOutlined),
    title: "¿Deseas descartar el progreso realizado?",
    content: "Al descartar, no se guardará la configuración que realizaste.",
    okText: "Si, descartar",
    cancelText: "No, seguir configurando",
    centered: true,
    onOk: () => {
      selectedExpiration.value = 30;
      customExpirationDays.value = 30;
      openGenerateMcpTokenModal.value = false;
    },
  });
};
</script>

<style scoped lang="sass">
p
  color: $gray-8
  margin-bottom: 16px

a
  display: inline-block

  span
    display: flex
    align-items: center

  img
    margin-left: 5px

.key__wrapper
  margin-top: 16px

.input__wrapper
  display: flex
  align-items: center
  gap: 12px

.input
  width: 100%
  max-width: min(450px, 100%)
  display: flex !important
  gap: 4px

.input2
  width: 280px
  display: flex !important
  gap: 4px

.action
  width: 170px

  .button
    width: 100%

.expires
  display: flex
  align-items: center
  gap: 2px
  margin-top: 8px
  margin-bottom: 0
  color: #00000073

.select-field
  display: flex
  flex-direction: column
  gap: 2px
  margin: 16px 0

  &__label
    display: flex
    align-items: center
    gap: 4px

  &__help
    color: #00000073
    margin-bottom: 0
</style>
